import React, { useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import TooltipAlertComponent from '../TooltipAlertComponent';
import FormInput from '../FormInput/index';
import { Field } from '../RowSimpleGrouped/styles';
import {
    getRefAnswersForSelectionFromKpi,
    parseListTreeItemIdsFromTextValue,
    fetchListTreeSelectOptionsByItemIds,
    fetchListTreeSelectOptionsForDirectChildrenOfParents,
    getAttrsForListTreeItemId,
    refNumberCalculationsDependencies,
} from '../../services/functions';

/**
 * UnitMeasure "Selection from kpi": opções vindas do KPI referência.
 * Quando houver listTreeID no KPI atual (configuração explícita) ou no ref ListTreeSelect,
 * resolve opções via ListTree/getItems.
 */
const SelectionFromKpiSelectField = ({
    sskpi,
    refSubSectionKPI,
    fallbackOptions,
    numberOfRows = 1,
    row = null,
    numberOfColumns = 1,
    column = null,
    moduleObject,
    setModuleObject,
    op,
    value,
    disabledField,
    loading,
    setText,
    subSectionKPIs,
    findValue,
    loadRefNumbersAndCalculations,
    readOnly,
    cultureTypeID = null,
    setSubsectionKPIs,
}) => {
    const listTreeRef = refSubSectionKPI?.unitMeasure?.description === 'ListTreeSelect';
    const currentHasExplicitListTree = !!sskpi?.listTreeID;
    const listTreeID = refSubSectionKPI?.listTreeID ?? sskpi?.listTreeID;
    const useListTreeResolve = !!listTreeID && (listTreeRef || currentHasExplicitListTree);

    const [resolvedOptions, setResolvedOptions] = useState([]);
    const [resolving, setResolving] = useState(false);
    const [resolvedSignature, setResolvedSignature] = useState(null);
    const attrsByItemIdRef = useRef(new Map());
    const subSectionKPIsRef = useRef(subSectionKPIs);
    subSectionKPIsRef.current = subSectionKPIs;
    const setModuleObjectRef = useRef(setModuleObject);
    setModuleObjectRef.current = setModuleObject;

    const listTreeResolveState = useMemo(() => {
        if (!useListTreeResolve || !refSubSectionKPI) return null;
        const refAnswers = getRefAnswersForSelectionFromKpi(
            moduleObject,
            sskpi.selectAnswersRefNumber,
            refSubSectionKPI,
            { numberOfRows, numberOfColumns, row, column },
        );
        const idOrder = [];
        const seen = new Set();
        refAnswers.forEach(a => {
            parseListTreeItemIdsFromTextValue(a.textValue).forEach(id => {
                if (!seen.has(id)) {
                    seen.add(id);
                    idOrder.push(id);
                }
            });
        });
        const ch = sskpi.selectionFromKpiListTreeDirectChildren === true || sskpi.selectionFromKpiListTreeDirectChildren === 'true' ? '1' : '0';
        const signature = `${refAnswers.map(a => `${a.textValue ?? ''}|${a.listTreeItemID ?? ''}`).join(';')}#${idOrder.join(',')}#ch:${ch}`;
        return { signature, idOrder };
    }, [
        moduleObject.answers,
        useListTreeResolve,
        refSubSectionKPI?.subSectionKPIID,
        sskpi.selectAnswersRefNumber,
        sskpi.selectionFromKpiListTreeDirectChildren,
        numberOfRows,
        numberOfColumns,
        row,
        column,
    ]);

    useEffect(() => {
        if (!useListTreeResolve || !refSubSectionKPI || !listTreeResolveState) {
            setResolvedOptions([]);
            setResolvedSignature(null);
            attrsByItemIdRef.current = new Map();
            return;
        }
        const { idOrder, signature } = listTreeResolveState;

        if (idOrder.length === 0) {
            setResolvedOptions([]);
            setResolvedSignature(signature);
            attrsByItemIdRef.current = new Map();
            return;
        }

        const useDirectChildren =
            sskpi.selectionFromKpiListTreeDirectChildren === true ||
            sskpi.selectionFromKpiListTreeDirectChildren === 'true';
        const usePathLabelForDirectChildren = listTreeRef;

        let cancelled = false;
        setResolving(true);
        const fetchOpts = useDirectChildren
            ? fetchListTreeSelectOptionsForDirectChildrenOfParents(listTreeID, idOrder, {
                  usePathLabel: usePathLabelForDirectChildren,
              })
            : fetchListTreeSelectOptionsByItemIds(listTreeID, idOrder);

        fetchOpts
            .then(({ options, attrsByItemId }) => {
                if (!cancelled) {
                    setResolvedOptions(options);
                    setResolvedSignature(signature);
                    attrsByItemIdRef.current = attrsByItemId || new Map();

                    const patch = {};
                    options.forEach(o => {
                        const id = Number(o.value);
                        if (Number.isNaN(id)) return;
                        const attrs = getAttrsForListTreeItemId(attrsByItemId, id);
                        if (attrs && typeof attrs === 'object') {
                            patch[String(id)] = attrs;
                        }
                    });

                    const setMo = setModuleObjectRef.current;
                    if (setMo && Object.keys(patch).length) {
                        setMo(prev => {
                            const already = prev.listTreeItemAttrsCache || {};
                            let hasNew = false;
                            for (const k of Object.keys(patch)) {
                                if (already[k] !== patch[k]) {
                                    hasNew = true;
                                    break;
                                }
                            }
                            if (!hasNew) return prev;
                            const mergedCache = { ...already, ...patch };
                            const moduleForCalc = {
                                ...prev,
                                listTreeItemAttrsCache: mergedCache,
                            };
                            const newAnswers = refNumberCalculationsDependencies(
                                sskpi,
                                prev.answers,
                                subSectionKPIsRef.current,
                                column,
                                row,
                                prev.version,
                                prev.columnRows,
                                moduleForCalc,
                            );
                            return {
                                ...prev,
                                listTreeItemAttrsCache: mergedCache,
                                answers: newAnswers,
                            };
                        });
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setResolvedOptions(
                        useDirectChildren
                            ? []
                            : idOrder.map(id => ({ value: String(id), text: String(id) })),
                    );
                    setResolvedSignature(signature);
                    attrsByItemIdRef.current = new Map();
                }
            })
            .finally(() => {
                if (!cancelled) setResolving(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        useListTreeResolve,
        listTreeID,
        refSubSectionKPI?.subSectionKPIID,
        sskpi.selectAnswersRefNumber,
        sskpi.selectionFromKpiListTreeDirectChildren,
        sskpi.refNumber,
        listTreeResolveState?.signature,
    ]);

    const options = useListTreeResolve ? resolvedOptions : (fallbackOptions || []);
    const placeholder = { value: '', text: 'Select an option' };

    useEffect(() => {
        const optionsForLog = options.map(o => ({
            value: o.value,
            label: o.label ?? o.text,
        }));
        console.log('[SelectionFromKpiSelectField] SubSectionKPI options', {
            subSectionKPIID: sskpi?.subSectionKPIID,
            refNumber: sskpi?.refNumber,
            selectAnswersRefNumber: sskpi?.selectAnswersRefNumber,
            useListTreeResolve,
            resolving,
            options: optionsForLog,
        });
    }, [options, sskpi?.subSectionKPIID, sskpi?.refNumber, sskpi?.selectAnswersRefNumber, useListTreeResolve, resolving]);
    const tooltipDisplay =
        value != null
            ? (options.find(o => String(o.value) === String(value))?.text ?? value)
            : '';

    useEffect(() => {
        if (!useListTreeResolve || resolving || readOnly || loading) return;
        if (!listTreeResolveState?.signature) return;
        if (resolvedSignature !== listTreeResolveState.signature) return;
        if (value == null || String(value).trim() === '') return;
        const existsInOptions = options.some(o => String(o.value) === String(value));
        if (existsInOptions) return;
        setText({
            subSectionKPI: sskpi,
            ref: sskpi.refNumber,
            value: '',
            row,
            column: numberOfColumns > 1 ? column : null,
            moduleObject,
            setModuleObject,
            subSectionKPIs,
            cultureTypeID: cultureTypeID ?? null,
            ...(setSubsectionKPIs ? { setSubsectionKPIs } : {}),
            listTreeItemID: null,
            listTreeAttrsCachePatch: null,
        });
    }, [
        useListTreeResolve,
        resolving,
        readOnly,
        loading,
        value,
        options,
        listTreeResolveState?.signature,
        resolvedSignature,
        sskpi,
        row,
        numberOfColumns,
        column,
        moduleObject,
        setModuleObject,
        subSectionKPIs,
        cultureTypeID,
        setSubsectionKPIs,
        setText,
    ]);

    const widthStyle =
        typeof calculateMaxOptionWidth === 'function' &&
        calculateMaxOptionWidth(options, placeholder)
            ? {
                  width: `${calculateMaxOptionWidth(options, placeholder)}px`,
                  minWidth: '180px',
              }
            : { width: '100%', minWidth: '180px' };

    return (
        <>
            {loadRefNumbersAndCalculations(sskpi)}
            <TooltipAlertComponent
                id={`${op}_alert`}
                message={sskpi.alertMessage}
                className={`alert-column ${findValue?.toleranceInvalid ? '' : 'hide'}`}
                placement={'left'}
            />
            <OverlayTrigger
                delay={{ hide: 0, show: 100 }}
                overlay={props =>
                    value != null || sskpi.inputMessage != null ? (
                        <Tooltip {...props}>
                            {value != null
                                ? tooltipDisplay
                                : sskpi.inputMessage
                                  ? sskpi.inputMessage
                                  : ''}
                        </Tooltip>
                    ) : (
                        <></>
                    )
                }
                placement={'left'}
            >
                <Field>
                    <FormInput
                        disabled={readOnly ? true : disabledField || resolving}
                        className={findValue?.toleranceInvalid ? 'alert-input' : ''}
                        options={options}
                        name={op}
                        id={op}
                        inputType="select"
                        placeholder={placeholder}
                        autoComplete="off"
                        value={value != null ? value : ''}
                        onChange={f => {
                            if (!readOnly && !loading) {
                                const raw = f.target.value;
                                const parsedId =
                                    raw != null && String(raw).trim() !== ''
                                        ? Number(raw)
                                        : NaN;
                                const listTreeItemID = !Number.isNaN(parsedId) ? parsedId : null;

                                let listTreeAttrsCachePatch = null;
                                if (listTreeItemID != null && attrsByItemIdRef.current?.size) {
                                    const attrsObj = getAttrsForListTreeItemId(
                                        attrsByItemIdRef.current,
                                        listTreeItemID,
                                    );
                                    if (attrsObj && typeof attrsObj === 'object') {
                                        listTreeAttrsCachePatch = {
                                            [String(listTreeItemID)]: attrsObj,
                                        };
                                    }
                                }

                                setText({
                                    subSectionKPI: sskpi,
                                    ref: sskpi.refNumber,
                                    value: raw,
                                    row,
                                    column: numberOfColumns > 1 ? column : null,
                                    moduleObject,
                                    setModuleObject,
                                    subSectionKPIs,
                                    cultureTypeID: cultureTypeID ?? null,
                                    ...(setSubsectionKPIs ? { setSubsectionKPIs } : {}),
                                    listTreeItemID,
                                    listTreeAttrsCachePatch,
                                });
                            }
                        }}
                    />
                    {sskpi.mandatory == true && (
                        <div class="required-icon">
                            <div class="text">*</div>
                        </div>
                    )}
                </Field>
            </OverlayTrigger>
        </>
    );
};

export default SelectionFromKpiSelectField;
